import React, { useContext, useEffect, useState, Fragment } from 'react'
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import { Link, useParams } from 'react-router-dom'
import EventContext from '../contexts/EventContext'
import { Menu, Transition } from '@headlessui/react'
import { ChevronDown } from 'lucide-react';

const flagEmoji = (code) =>
    code ? code.toUpperCase().split('').map(c => String.fromCodePoint(0x1F1A5 + c.charCodeAt(0))).join('') : ''

const lngs = {
    fr: { nativeName: 'Français', idFlag: 'fr' },
    en: { nativeName: 'English', idFlag: 'gb' },
    es: { nativeName: 'Espagnol', idFlag: 'es' },
    de: { nativeName: 'Deutsch', idFlag: 'de' },
    pt: { nativeName: 'Portugal', idFlag: 'pt' },
    it: { nativeName: 'Italia', idFlag: 'it' },
    ja: { nativeName: 'Japan', idFlag: 'jp' },
    ko: { nativeName: 'Korea', idFlag: 'kr', iplocate: 'ko' },
    nl: { nativeName: 'Nederlands', idFlag: 'nl' },
    hi: { nativeName: 'Hindi', idFlag: 'in' },
    iw: { nativeName: 'Hebrew', idFlag: 'il' },
    ar: { nativeName: 'Arabic', idFlag: '' },
    'zh-CN': { nativeName: 'china', idFlag: 'cn' }
}

export const NavTranslationLanguageSelect = ({ displayLanguageName = false }) => {

    const eventContext = useContext(EventContext);
    const [personalizedLngs, setPersonalizedLngs] = useState(lngs);
    const { currentEvent } = eventContext;

    useEffect(() => {
        if (currentEvent && [2146, 2269].includes(Number(currentEvent.id_event))) {
            setPersonalizedLngs({
                fr: { nativeName: 'Français', idFlag: 'fr' },
                en: { nativeName: 'English', idFlag: 'gb' },
                it: { nativeName: 'Italia', idFlag: 'it' },
            })
        }
    }, [i18n.resolvedLanguage, currentEvent]);

    return (
        <div className="flex z-50">
            <Menu as="div" className="relative inline-block text-left">
                <div>
                    <Menu.Button className="inline-flex w-full justify-center items-center gap-x-1.5 rounded-2xl dark:bg-neutral-900 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-neutral-700 hover:bg-gray-50 dark:hover:bg-neutral-800 uppercase">
                        <span className="text-base leading-none">
                            {flagEmoji(personalizedLngs[i18n.resolvedLanguage]?.idFlag || '')}
                        </span>
                        {personalizedLngs[i18n.resolvedLanguage]?.idFlag == '' ? personalizedLngs[i18n.resolvedLanguage]?.nativeName : ''}
                        {displayLanguageName && <span className="ml-1">{personalizedLngs[i18n.resolvedLanguage]?.nativeName}</span>}
                        <ChevronDown className="-mr-1 h-4 w-4 text-gray-400" aria-hidden="true" />
                    </Menu.Button>
                </div>

                <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                >
                    <Menu.Items className="absolute right-0 z-[60] mt-2 w-40 origin-top-right rounded-md  dark:bg-neutral-900 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none max-h-96 overflow-y-auto">
                        <div className="py-1">
                            {Object.keys(personalizedLngs).filter(key => ['fr', 'en', 'es', 'de', 'pt', 'nl', 'it', 'ko', 'ja', 'zh-CN', 'hi', 'iw', 'ar'].includes(key)).map(lng => (
                                <Menu.Item key={lng}>
                                    {({ active }) => (
                                        <button
                                            type="button"
                                            onClick={() => { i18n.changeLanguage(lng); console.log(lng) }}
                                            className={`
                                                ${active ? 'bg-gray-100 dark:bg-neutral-800 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-200'}
                                                block w-full px-4 py-2 text-left text-sm flex items-center
                                            `}
                                            style={{ fontWeight: i18n.resolvedLanguage === lng ? 'bold' : 'normal' }}
                                        >
                                            <span className="text-base leading-none mr-2">
                                                {flagEmoji(personalizedLngs[lng].idFlag || '')}
                                            </span>
                                            {personalizedLngs[lng].nativeName}
                                        </button>
                                    )}
                                </Menu.Item>
                            ))}
                        </div>
                    </Menu.Items>
                </Transition>
            </Menu>
        </div>
    )
}

const NavTranslation = () => {

    const eventContext = useContext(EventContext);
    const { currentEvent } = eventContext;
    const { id_cycle_lang } = useParams();
    const { t } = useTranslation();

    useEffect(() => {
        // Charge le script GTranslate à chaque changement de langue
        if (!document.getElementById("gtranslate-script")) {
            const script = document.createElement("script");
            script.id = "gtranslate-script";
            script.src = "https://cdn.gtranslate.net/widgets/latest/dwf.js";
            script.defer = true;
            document.body.appendChild(script);
        } else {
            // Si déjà chargé, on peut forcer la langue si besoin
            if (window.GTranslate && typeof window.GTranslate.setLanguage === "function") {
                window.GTranslate.setLanguage(i18n.resolvedLanguage);
            }
        }
    }, [i18n.resolvedLanguage]);

    return (
        <div className="flex flex-col lg:flex-row-reverse items-center py-2 pe-3 dark:bg-neutral-950 gap-2">
            <NavTranslationLanguageSelect displayLanguageName={true} />

            {!["2270", '3014', '3015'].includes(id_cycle_lang) &&
                <Link to={currentEvent ? `/events/${currentEvent.id_event}/login` : ' / login'}
                    className={`${currentEvent?.event_categorie == "cdt" ? "hidden" : ''} text-lg items-center justify-center rounded-full border-pink-600 hover:border-pink-500 border-2 px-5 py-1 dark:text-white hidden`}   >
                    {t('global.connexion')}
                </Link>
            }
            {currentEvent && currentEvent?.event_etat !== "bilan" && !["2270", "3013", "3014", "3015"].includes(id_cycle_lang) &&
                <Link
                    style={{ textTransform: "capitalize" }}
                    className={`${currentEvent?.event_categorie == "cdt" || currentEvent ? "hidden" : ''} text-lg items-center justify-center rounded-full bg-pink-600 hover:bg-pink-500 px-5 py-1 font-bold text-white transition-colors`}
                    to={`/events/${currentEvent?.id_event}/registration`}
                >
                    {t('event_nav.inscription')}
                </Link>
            }
        </div>
    )
}

export default NavTranslation